import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { GitCommit, RotateCcw, Rocket } from "lucide-react";

export function Checkpoints({ projectId }) {
  const [list, setList] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}/checkpoints`); setList(data); } catch {}
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post(`/projects/${projectId}/checkpoints`, {}); toast.success("Checkpoint saved"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async (id) => {
    try { await api.post(`/checkpoints/${id}/restore`); toast.success("Restored (non-destructive)"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="k-container" data-testid="checkpoints" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <GitCommit size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Checkpoints</h3>
        <button className="k-btn k-btn-ghost" data-testid="create-checkpoint" style={{ marginLeft: "auto", padding: "6px 14px", fontSize: 13 }} onClick={create}>Take checkpoint</button>
      </div>
      {list.length === 0 ? <p className="k-muted" style={{ fontSize: 13 }}>No checkpoints yet. Take one before a risky change.</p> :
        list.map(c => (
          <div key={c.id} className="k-control" data-testid={`checkpoint-${c.id}`}
            style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="k-heading" style={{ fontSize: 13 }}>{c.name}<span className="k-muted" style={{ fontSize: 11 }}> · {String(c.at).slice(0, 19).replace("T", " ")}</span></span>
            <button className="k-link" data-testid={`restore-${c.id}`} onClick={() => restore(c.id)}><RotateCcw size={14} /> Restore</button>
          </div>
        ))}
    </div>
  );
}

export function PublishGate({ projectId, onPublished }) {
  const [gate, setGate] = useState(null);
  const [url, setUrl] = useState("");
  const [ack, setAck] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}/publish-gate`); setGate(data); } catch {}
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/publish`, { live_url: url, acknowledge_non_critical: ack });
      toast.success("Marked Published/Deployed"); onPublished?.(data); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!gate) return null;

  return (
    <div className="k-container" data-testid="publish-gate" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Rocket size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Publish gate</h3>
      </div>
      {gate.open_items.critical.length > 0 && (
        <div className="k-control" data-testid="gate-critical" style={{ marginBottom: 8 }}>
          <div className="k-tag-red" style={{ fontSize: 13, fontWeight: 700 }}>Critical — publishing blocked</div>
          {gate.open_items.critical.map((c, i) => <div key={i} className="k-muted" style={{ fontSize: 12 }}>• {c}</div>)}
        </div>
      )}
      {gate.open_items.non_critical.length > 0 && (
        <div className="k-control" data-testid="gate-non-critical" style={{ marginBottom: 8 }}>
          <div className="k-tag-warn" style={{ fontSize: 13, fontWeight: 700 }}>Non-critical — needs acknowledgement</div>
          {gate.open_items.non_critical.map((c, i) => <div key={i} className="k-muted" style={{ fontSize: 12 }}>• {c}</div>)}
          <label className="k-muted" style={{ fontSize: 12, display: "flex", gap: 6, marginTop: 6 }}>
            <input type="checkbox" data-testid="ack-non-critical" checked={ack} onChange={e => setAck(e.target.checked)} />
            I acknowledge these non-critical items and choose to publish.
          </label>
        </div>
      )}
      <input className="k-control" data-testid="live-url" placeholder="Live deployment URL (https://…)" value={url}
        onChange={e => setUrl(e.target.value)} style={{ marginBottom: 8 }} />
      <button className="k-btn k-btn-primary" data-testid="publish-btn" onClick={publish}
        disabled={!gate.can_publish || !url}>Mark Published/Deployed</button>
      <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>{gate.note}</p>
    </div>
  );
}
